import React, { useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import "../../../../assets/styles/custom-styles.css";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import useBaseService from "../../../../hooks/services/useBaseService";
import { locationFields } from "../../../../constants/forms/locationFields";
import Form from "../../../../components/forms/Form";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cleanPayload } from "../../../../utils/objectUtils";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditCustomerLocationPage = () => {
  const navigate = useNavigate();
  const { id, addressId, locationId } = useParams();
  const location = useLocation();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues(locationFields),
  );
  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const queryClient = useQueryClient();

  // --- Query para buscar os dados da Localização ---
  const { data: locationData, isLoading: isLoadingLocation } =
    useQueryWithLoader({
      queryKey: ["customerLocation", id, addressId, locationId],
      queryFn: async () => {
        const response = await baseServiceGetByColumn(
          entities.customers.customerAddressLocationUpdate(
            id,
            addressId,
            locationId,
          ),
        );
        return response.result;
      },
      enabled: !!id && !!addressId && !!locationId,
      staleTime: 5 * 60 * 1000,
    });

  useEffect(() => {
    if (locationData) {
      setFormData({
        location: locationData,
      });
    }
  }, [locationData]);

  // --- Mutation para atualizar os dados da Localização ---
  const updateLocationMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.customers.customerAddressLocationUpdate(
          id,
          addressId,
          locationId,
        ),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        navigate(`/aluguel/clientes/${id}/endereco/${addressId}/localizacoes`, {
          state: { type: "success" },
        });
        queryClient.invalidateQueries({
          queryKey: ["customerLocation", id, addressId, locationId],
        });
        queryClient.invalidateQueries({
          queryKey: ["customerLocations", id, addressId],
        });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        location: {
          ...cleanedData.location,
        },
      };
      await updateLocationMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao atualizar localização:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateLocationMutation]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/clientes/${id}/endereco/${addressId}/localizacoes`);
  }, [id, addressId, navigate]);

  // --- Efeito para exibir notificações da rota (se houver) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const isPageLoading = isLoadingLocation;
  const isSubmitting = updateLocationMutation.isPending;

  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return locationFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Localização"
        showBackButton={true}
        backUrl={`/aluguel/clientes/${id}/endereco/${addressId}/localizacoes`}
      />

      <div className="container-fluid p-1">
        <Form
          handleBack={handleBack}
          onSubmit={handleSubmit}
          textSubmit="Editar"
          textLoadingSubmit="Editando"
          initialFormData={formData}
          isLoading={isPageLoading || isSubmitting}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditCustomerLocationPage;
